import apolloClient from 'api/apolloClient';
import { EDateZipInfo, GroupInfo, Policy, File, GenerationStep } from 'types/policyService';
import {
  GENERATE_EDI_MANUALLY,
  GET_ADDITIONAL_OUTPUTS_BY_PERIOD_ID,
  GET_ADDITIONAL_OUTPUT_ZIPS_BY_PERIOD_IDS,
  GET_FINAL_ZIPS_BY_PERIOD_IDS,
  GET_GROUPS_INFO_BY_DATE,
  GET_PDFS_FROM_POLICY,
  GET_POLICY_BY_GROUP_ID,
  GET_POLICIES_BY_RANGE,
  SET_GENERATION_STEP,
  SIGNATURE_REQUEST_FOR_FILE,
  TRIGGER_ALLIED_CONFIRMATION_LETTER,
  TRIGGER_ALLIED_WELCOME_LETTER,
} from 'api/queries/policyService';
import axios from 'axios';
import { PaginationDTO } from 'types/general';
import { FetchResult } from 'apollo-link';
import { COMMON_URLS } from '@insurancemenu/imenu-components-lib/dist/utils/urls';

interface ZipProps {
  url: string;
}

export async function getPolicyByEnrollmentPeriodId(periodId: string): Promise<Policy> {
  const res = await apolloClient.query<{ getPolicyByEnrollmentId: Policy }>({
    query: GET_PDFS_FROM_POLICY,
    fetchPolicy: 'no-cache',
    variables: { periodId },
  });
  return res.data.getPolicyByEnrollmentId;
}

export async function getPolicyByGroupId(groupId: string): Promise<Policy> {
  const res = await apolloClient.query<{ getPolicyByGroupId: Policy }>({
    query: GET_POLICY_BY_GROUP_ID,
    fetchPolicy: 'no-cache',
    variables: { groupId },
  });
  return res.data.getPolicyByGroupId;
}

export async function getAdditionalOutputsByPeriodId(periodId: string, subscriberId?: string): Promise<File[]> {
  const res = await apolloClient.query<{ getAdditionalOutputsByPeriodId: File[] }>({
    query: GET_ADDITIONAL_OUTPUTS_BY_PERIOD_ID,
    fetchPolicy: 'no-cache',
    variables: { periodId, subscriberId },
  });
  return res.data.getAdditionalOutputsByPeriodId;
}

export async function getFinalZipsByPeriodIds(periodIds: string[]): Promise<EDateZipInfo[]> {
  const res = await apolloClient.query<{ getFinalZipsByPeriodIds: EDateZipInfo[] }>({
    query: GET_FINAL_ZIPS_BY_PERIOD_IDS,
    fetchPolicy: 'no-cache',
    variables: { periodIds },
  });
  return res.data.getFinalZipsByPeriodIds;
}

export async function getAdditionalOutputsZipByPeriodIds(periodIds: string[]): Promise<EDateZipInfo[]> {
  const res = await apolloClient.query<{ getAdditionalOutputsZipByPeriodIds: EDateZipInfo[] }>({
    query: GET_ADDITIONAL_OUTPUT_ZIPS_BY_PERIOD_IDS,
    fetchPolicy: 'no-cache',
    variables: { periodIds },
  });
  return res.data.getAdditionalOutputsZipByPeriodIds;
}

export async function getPreSignUrl(id: string): Promise<string> {
  return (
    await axios.get<ZipProps>(`https://${COMMON_URLS.api}/pdfRead/Prod/downloadZip/${id}`, {
      withCredentials: true,
    })
  ).data.url;
}

export async function getGroupsInfoByDate(from: string, to: string, fullView: boolean): Promise<GroupInfo[]> {
  const res = await apolloClient.query<{ getGroupsInfoBy: GroupInfo[] }>({
    query: GET_GROUPS_INFO_BY_DATE,
    fetchPolicy: 'no-cache',
    variables: { from, to, fullView },
  });
  return res.data.getGroupsInfoBy;
}

export async function getPoliciesByRange(
  from: string,
  to: string,
  sponsorId: string,
  step: GenerationStep | string,
  page: number,
): Promise<PaginationDTO<Policy>> {
  const res = await apolloClient.query<{ getPoliciesByRange: PaginationDTO<Policy> }>({
    query: GET_POLICIES_BY_RANGE,
    fetchPolicy: 'no-cache',
    variables: { from, to, sponsorId, step, page },
  });
  return res.data.getPoliciesByRange;
}

export async function setGenerationStep(
  policyId: string,
  step: GenerationStep,
): Promise<FetchResult<{ setGenerationStep: Policy }>> {
  return apolloClient.mutate<{ setGenerationStep: Policy }>({
    mutation: SET_GENERATION_STEP,
    variables: { policyId, step },
  });
}

export async function generateEdiManually(
  sponsorId: string,
  policyIds: string[],
): Promise<FetchResult<{ generateEdiManually: boolean }>> {
  return apolloClient.mutate<{ generateEdiManually: boolean }>({
    mutation: GENERATE_EDI_MANUALLY,
    variables: { sponsorId, policyIds },
  });
}

export async function requestForSignature(
  file: File,
  periodId: string,
): Promise<FetchResult<{ signatureRequestForFile: boolean }>> {
  return apolloClient.mutate<{ signatureRequestForFile: boolean }>({
    mutation: SIGNATURE_REQUEST_FOR_FILE,
    variables: { file, periodId },
  });
}

export async function triggerAlliedWelcomeLetter(
  periodId: string,
): Promise<FetchResult<{ triggerAlliedWelcomeLetter: boolean }>> {
  return apolloClient.mutate<{ triggerAlliedWelcomeLetter: boolean }>({
    mutation: TRIGGER_ALLIED_WELCOME_LETTER,
    variables: { periodId },
  });
}

export async function triggerAlliedConfirmationLetter(
  periodId: string,
): Promise<FetchResult<{ triggerAlliedConfirmationLetter: boolean }>> {
  return apolloClient.mutate<{ triggerAlliedConfirmationLetter: boolean }>({
    mutation: TRIGGER_ALLIED_CONFIRMATION_LETTER,
    variables: { periodId },
  });
}
